import * as THREE from 'three'
import { EdgesGeometry, LineBasicMaterial, LineSegments } from 'three'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'
import * as BGU from 'three/examples/jsm/utils/BufferGeometryUtils.js'

const stlLoader   = new STLLoader()

/**
 * @param {File} stlFile
 * @returns {Promise<{ imgFile: File }>}
 */
export const generateImageFromStl = (stlFile) => new Promise((resolve, reject) => {
	const reader = new FileReader()
	reader.onload = async (event) => {
		try {
			const buffer = event.target?.result
			if (!(buffer instanceof ArrayBuffer)) throw new Error('Failed to read as ArrayBuffer')
			const dataUrl = renderGeometryToDataUrl(stlLoader.parse(buffer))
			const blob    = await fetch(dataUrl).then(r => r.blob())
			const nameNoExt = stlFile.name.replace(/\.[^/.]+$/, '')
			const imgFile = new File([blob], `${nameNoExt}.png`, { type: 'image/png' })
			resolve({ imgFile })
		} catch (error) { reject(error) }
	}
	reader.onerror = () => reject(new Error('FileReader error'))
	reader.readAsArrayBuffer(stlFile)
})

/** @param {THREE.BufferGeometry} geometry @returns {string} */
const renderGeometryToDataUrl = (geometry) => {
	const scene = new THREE.Scene()
	scene.background = null
	geometry.center()
	geometry.computeBoundingSphere()
	const { radius } = geometry.boundingSphere
	if (!radius) throw new Error('Could not compute bounding sphere')

	const camera = new THREE.OrthographicCamera(-radius, radius, radius, -radius, 0.001, 100000)
	camera.position.set(radius, radius, radius)
	camera.lookAt(new THREE.Vector3(0, 0, 0))
	camera.updateMatrixWorld()
	camera.updateProjectionMatrix()

	const colors = ['#A5A5A5', '#EAEAEA', '#C4E2F3', '#F88701', '#7F7F7F', '#FAB601']
	const color  = colors[Math.floor(Math.random() * colors.length)]

	const geo  = BGU.mergeVertices(geometry, 1e-4)
	geo.computeVertexNormals()

	const mesh = new THREE.Mesh(geo, new THREE.MeshPhongMaterial({
		color: new THREE.Color(color), specular: 0x333333, shininess: 150, flatShading: false,
	}))
	scene.add(mesh)
	mesh.add(new LineSegments(
		new EdgesGeometry(geometry, 30),
		new LineBasicMaterial({ color: 0x000000, linewidth: 2, transparent: true, opacity: 0.8 }),
	))

	const l1 = new THREE.DirectionalLight(0xffffff, 0.8); l1.position.set(-1, 5, 1); scene.add(l1)
	const l2 = new THREE.DirectionalLight(0xffffff, 0.6); l2.position.set(1, -1, 1); scene.add(l2)
	const la = new THREE.AmbientLight(0x404040, 0.8); la.position.set(1, 1, -1); scene.add(la)

	const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
	renderer.setSize(500, 500)
	renderer.render(scene, camera)
	const dataUrl = renderer.domElement.toDataURL()
	renderer.dispose()
	return dataUrl
}
